import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Star, Heart, Zap, ArrowRight, CheckCircle, ChevronDown,
  Sun, Waves
} from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { useTheme, themeOrder } from "../lib/theme";

const PORTAL_SIGNUP_URL = "https://portal.teacherhappy.com/signup";
const TURNSTILE_SITE_KEY = "0x4AAAAAACr8W0IQe6Ty-adX";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Theme Pill Switcher Component
const ThemePillSwitcher = () => {
  const { currentTheme, switchTheme } = useTheme();
  const { t } = useTranslation();

  const themeIcons = {
    sunshine: Sun,
    ocean: Waves
  };

  return (
    <div className="theme-pill-container" data-testid="theme-switcher">
      {themeOrder.map((themeKey) => {
        const Icon = themeIcons[themeKey];
        return (
          <button
            key={themeKey}
            onClick={() => switchTheme(themeKey)}
            className={`theme-pill-btn ${currentTheme === themeKey ? 'active' : ''}`}
            data-testid={`theme-${themeKey}`}
          >
            <span className="flex items-center gap-1.5">
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t(`themes.${themeKey}`)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

// Mock form submission (no database)
const mockFormSubmit = async (formData) => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // Log to console for debugging
  console.log('Form submitted (mock):', formData);
  
  // Always succeed
  return { success: true, data: formData };
};

// Hero Section
export const HeroSection = () => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const [formData, setFormData] = useState({ full_name: '', email: '', wechat_id: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Initialize Turnstile widget
  useEffect(() => {
    let mounted = true;
    
    const initTurnstile = () => {
      if (!mounted || !turnstileRef.current || turnstileWidgetId.current !== null) return;
      
      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => {
            if (mounted) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (mounted) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (mounted) setTurnstileToken(null);
          },
          theme: 'light',
        });
        setTurnstileLoaded(true);
      } catch (e) {
        console.log('Turnstile init error:', e);
      }
    };

    // Wait for Turnstile script to load
    const checkForTurnstile = () => {
      if (window.turnstile) {
        initTurnstile();
      } else {
        setTimeout(checkForTurnstile, 200);
      }
    };
    
    checkForTurnstile();

    return () => {
      mounted = false;
      if (turnstileWidgetId.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {}
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.full_name || isSubmitting) return;
    
    // Check Turnstile verification
    if (!turnstileToken) {
      toast.error(t('toast.verifyHuman') || "Please complete the verification");
      return;
    }

    setIsSubmitting(true);
    try {
      // Mock submission - no database
      await mockFormSubmit({
        full_name: formData.full_name,
        email: formData.email,
        wechat_id: formData.wechat_id || null,
        turnstile_token: turnstileToken
      });
      
      setIsSubmitted(true);
      
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#FFD700', '#007AFF', '#FF6B6B']
      });
      
      // Show success toast
      toast.success(t('toast.success') || "Ready to Learn? 🎉");
      
      // Build URL with parameters for portal handshake
      const params = new URLSearchParams({
        name: formData.full_name,
        email: formData.email,
        theme: currentTheme,
        cf_token: turnstileToken
      });
      if (formData.wechat_id) {
        params.append('wechat', formData.wechat_id);
      }
      
      // Redirect to Portal signup with pre-filled data
      setTimeout(() => {
        window.location.href = `${PORTAL_SIGNUP_URL}?${params.toString()}`;
      }, 3000);
    } catch (error) {
      toast.error(t('toast.error') || "Something went wrong. Please try again.");
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="hero" 
      data-testid="hero-section"
      className="hero-bg min-h-screen flex items-center pt-20"
    >
      <div className="absolute top-20 right-0 w-96 h-96 blob-yellow"></div>
      <div className="absolute bottom-20 left-0 w-80 h-80 blob-blue"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center lg:text-left"
          >
            <motion.div variants={fadeInUp} className="mb-4">
              <span 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                style={{ backgroundColor: 'var(--th-background-alt)', color: 'var(--th-secondary)' }}
              >
                <Star className="w-4 h-4" /> {t('hero.badge')}
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ color: 'var(--th-text-primary)' }}
            >
              <span style={{ color: 'var(--th-text-primary)' }}>{t('hero.title1')} </span>
              <span style={{ color: 'var(--th-secondary)' }}>{t('hero.title2')}</span>{" "}
              <span style={{ color: 'var(--th-primary)' }}>{t('hero.title3')}</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg sm:text-xl mb-8 max-w-xl mx-auto lg:mx-0"
              style={{ color: 'var(--th-text-secondary)' }}
            >
              {t('hero.subtitle')}
            </motion.p>
            
            <motion.form 
              variants={fadeInUp}
              onSubmit={handleSubmit}
              className={`flex flex-col gap-4 max-w-md mx-auto lg:mx-0 ${isSubmitted ? 'form-success' : ''}`}
              data-testid="signup-form"
            >
              {!isSubmitted ? (
                <>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder={t('hero.namePlaceholder') || "Full Name"}
                    className="input-default"
                    required
                    data-testid="name-input"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('hero.emailPlaceholder')}
                    className="input-default"
                    required
                    data-testid="email-input"
                  />
                  <input
                    type="text"
                    name="wechat_id"
                    value={formData.wechat_id}
                    onChange={handleInputChange}
                    placeholder={t('hero.wechatPlaceholder') || "WeChat ID (optional)"}
                    className="input-default"
                    data-testid="wechat-input"
                  />
                  
                  {/* Cloudflare Turnstile Widget */}
                  <div 
                    ref={turnstileRef} 
                    className="cf-turnstile flex justify-center"
                    data-testid="turnstile-widget"
                  ></div>
                  
                  <button 
                    type="submit" 
                    className="btn-primary whitespace-nowrap flex items-center justify-center gap-2"
                    disabled={isSubmitting || !turnstileToken}
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <span className="animate-pulse">{t('hero.submitting')}</span>
                    ) : (
                      <>{t('hero.submitButton')} <ArrowRight className="w-5 h-5" /></>
                    )}
                  </button>
                </>
              ) : (
                <div 
                  className="flex items-center gap-3 rounded-full px-6 py-4 font-semibold"
                  style={{ 
                    backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    color: '#16A34A'
                  }}
                  data-testid="success-message"
                >
                  <CheckCircle className="w-6 h-6" />
                  {t('hero.successMessage') || "Ready to Learn? 🎉 Redirecting..."}
                </div>
              )}
            </motion.form>

            <motion.p variants={fadeInUp} className="text-sm mt-4" style={{ color: 'var(--th-text-muted)' }}>
              {t('hero.freeGift')}
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1769201153045-98827f62996b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGVsZW1lbnRhcnklMjBraWRzJTIwanVtcGluZyUyMGRhbmNpbmclMjBjbGFzc3Jvb218ZW58MHx8fHwxNzczMTYyNTU0fDA&ixlib=rb-4.1.0&q=85"
                alt="Happy diverse children learning together"
                className="w-full h-auto rounded-3xl shadow-2xl image-blob-mask"
                data-testid="hero-image"
              />
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 -right-4 rounded-2xl p-4 shadow-xl"
                style={{ backgroundColor: 'var(--th-card-bg)' }}
              >
                <div className="flex items-center gap-2">
                  <Heart className="w-6 h-6 text-red-500 fill-red-500" />
                  <span className="font-heading font-bold" style={{ color: 'var(--th-text-primary)' }}>{t('hero.k1k12')}</span>
                </div>
              </motion.div>
              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -bottom-4 -left-4 rounded-2xl p-4 shadow-xl"
                style={{ backgroundColor: 'var(--th-primary)' }}
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-6 h-6" style={{ color: '#1E293B' }} />
                  <span className="font-heading font-bold" style={{ color: '#1E293B' }}>{t('hero.highEnergy')}</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
        
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex justify-center mt-12"
        >
          <a href="#method" style={{ color: 'var(--th-text-muted)' }}>
            <ChevronDown className="w-8 h-8" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
